import { useCallback, useMemo } from 'react';

import keys from 'lodash/keys';
import mapValues from 'lodash/mapValues';
import isEmpty from 'lodash/isEmpty';

import { useQueryParams } from '../hooks/useQueryParams';
import Base64 from '../utils/base64';
import { log } from '../utils/log';
import { ProductFilter } from '../../../models/Product';
import { usePaginationAndSortParams } from '../hooks/usePaginationAndSortParams';

/**
 * Manages all filters and provides ways to set or unset them.
 */
export const useProductFilters = () => {
    const { filters, setFilters } = useFiltersQueryParam();
    const { setPage } = usePaginationAndSortParams();

    const resetAllFilters = useCallback(() => {
        setFilters({});
        setPage(0);
    }, [setFilters, setPage]);

    const changeFilter = useCallback(
        (category: string, includeFilterValues: ReadonlyArray<string>, excludeFilterValues: ReadonlyArray<string>) => {
            if (isEmpty(includeFilterValues) && isEmpty(excludeFilterValues)) {
                const newFilters = { ...filters };
                delete newFilters[category];
                setFilters(newFilters);
            } else {
                setFilters({
                    ...filters,
                    [category]: {
                        includeFilterValues: [...includeFilterValues],
                        excludeFilterValues: [...excludeFilterValues]
                    }
                });
            }
            setPage(0);
        },
        [filters, setFilters, setPage]
    );

    return {
        filters,
        changeFilter,
        resetAllFilters
    };
};

/**
 * Manages the selected filters via the query parameter 'filter'.
 */
const useFiltersQueryParam = () => {
    const {
        filter: { value, set }
    } = useQueryParams({ filter: 'string' });

    const filters = useMemo(() => deserializeFilter(value), [value]);

    const setFilters = useCallback(
        (filter: ProductFilter) => {
            const newValue = serializeFilter(filter);

            if (newValue) {
                set(newValue);
            } else {
                set(undefined);
            }
        },
        [set]
    );

    return {
        filters,
        setFilters
    };
};

/**
 * Serializes a filter to a query parameter. Performs a mapping from 'includeFilterValues' to 'include' and
 * 'excludeFilterValues' to 'exclude' to be consistent with the way filters are stored as a query parameter.
 */
export function serializeFilter(filter: ProductFilter): string {
    if (keys(filter).length === 0) {
        return '';
    }

    const sanitized = mapValues(filter, filterSetting => ({
        include: filterSetting.includeFilterValues,
        exclude: filterSetting.excludeFilterValues
    }));

    return Base64.encode(JSON.stringify(sanitized));
}

/**
 * Deserializes a filter query parameter to a ProductFilter. Performs a mapping from 'include' to 'includeFilterValues'
 * and 'exclude' to 'excludeFilterValues' to be consistent with the way filters are stored as a query parameter.
 */
export function deserializeFilter(filter: string | undefined): ProductFilter {
    if (!filter) {
        return {};
    }

    try {
        const parsed = JSON.parse(Base64.decode(filter));

        return mapValues(parsed, filterSetting => ({
            includeFilterValues: filterSetting.include,
            excludeFilterValues: filterSetting.exclude
        }));
    } catch (e) {
        log.info('Cannot parse query filters', { filter });
        return {};
    }
}

export function isEmptyProductFilters(filters: ProductFilter) {
    return isEmpty(filters);
}
